// cleanup_hoc_to_hooks_PAYMENT_MAPS
// cleanup_remove_external_libraries_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { isEqual, pickBy, get } from 'lodash';
import classnames from 'classnames';

import Alert from '@glu/alerts-react';
import { useValidation } from '@glu/validation-react';
import { CheckboxRadio, CheckboxRadioGroup } from '@glu/form-components';

import { WORKSPACES, ALERTVARIATIONS, DEFAULT_PROPS } from 'constants.js';
import useLocale from 'components/useLocale';
import { unsubscribeComponent } from 'components/utils/util';

import { createInitialFormData, storeFormReducer } from './reducer.storeFormData';
import Header from './Header';
import useStyles from './Header.styles';
import Table from './Table';
import Footer from './Footer';
import {
  clearFormError,
  atLeastOneUpdateAndMatchValidation,
  validateMatchAndUpdateCheckboxes,
  formInitialStateFromApiData,
  formAPIData,
  sendACHMap,
  castFieldsToNumber,
  deleteEmptyField
} from './utils';
import { MAP_FORMAT } from './utils.mapFormats';
import { validateTransactionTypeRow } from './TransactionRows/utils';
import {
  API_DETAIL_FIELDS_VOCABULARY,
  HEADER_FIELDS,
  STORE_FORM_ACTIONS,
  TRANSACTION_FIELD_VARIANT
} from './constants';
import { PROCESS_MODES_NAMES } from '../utils';

const { EMPTY_ARRAY, EMPTY_FUNCTION, EMPTY_OBJ } = DEFAULT_PROPS;

export const CreateACHImportMapComponent = ({
  accountTypesData = EMPTY_ARRAY,
  achDelimitersData = EMPTY_ARRAY,
  achImportMapData = null,
  closeModal = EMPTY_FUNCTION,
  endOfRecordsData = EMPTY_ARRAY,
  history = EMPTY_OBJ,
  mapCode,
  modify = false,
  processMode = '',
  sending = false,
  setError = EMPTY_FUNCTION,
  setLeavingPage = EMPTY_FUNCTION,
  setSending = EMPTY_FUNCTION,
  transactionCodesData = EMPTY_ARRAY
}) => {
  const subscriptions = useRef({});
  const locale = useLocale();
  const classes = useStyles();
  const [formErrors, setFormErrors] = useState([]);
  const [tableComponentKey, resetTableComponentKey] = useState(Date.now());
  const [transactionFieldVariant, changeTransactionFieldVariant] = useState(
    achImportMapData?.achImportMapDetails?.find(
      (field) =>
        field.fieldName === API_DETAIL_FIELDS_VOCABULARY.transactionCode &&
        (field?.fieldPosNumber || field?.startPosition || field?.replacementValue)
    )
      ? TRANSACTION_FIELD_VARIANT.TRANSACTION_CODE
      : TRANSACTION_FIELD_VARIANT.TRAN_TYPE
  );

  const [mapFormat, setMapFormat] = useState(
    get(achImportMapData, 'achImportMapHeader.mapType', MAP_FORMAT.DELIMITED).toUpperCase()
  );

  const initialData = useMemo(
    // On MODIFY - returns modify data, on CREATE - returns default data object
    () =>
      formInitialStateFromApiData({
        achImportMapData,
        achDelimitersData,
        endOfRecordsData,
        transactionCodesData,
        accountTypesData,
        mapFormat
      }),
    [
      achImportMapData,
      achDelimitersData,
      endOfRecordsData,
      transactionCodesData,
      mapFormat,
      accountTypesData
    ]
  );

  const initialDelimitedFormData = useRef(
    createInitialFormData({
      allowedMapFormat: MAP_FORMAT.DELIMITED,
      mapFormat,
      modify,
      initialData
    })
  );
  const initialFixedFormData = useRef(
    createInitialFormData({
      allowedMapFormat: MAP_FORMAT.FIXED,
      mapFormat,
      modify,
      initialData
    })
  );
  const initialFormsData = {
    delimited: initialDelimitedFormData.current,
    fixed: initialFixedFormData.current
  };
  const [storedFormData, storeFormData] = useReducer(storeFormReducer, initialFormsData);

  const controlledFormData =
    mapFormat === MAP_FORMAT.DELIMITED ? storedFormData.delimited : storedFormData.fixed;
  const [
    FormContextProvider,
    formContext,
    {
      data: formState,
      onChange,
      registerValidators,
      resetData,
      resetErrors,
      unregisterValidators,
      validate
    }
  ] = useValidation({ data: controlledFormData });

  const handleError = ({ data }) => {
    setError(get(data, '[0].code'));
    return setSending(false);
  };

  const handleSuccess = ({ success }, modify) => {
    setLeavingPage(true);

    return history.push({
      pathname: history?.location?.state?.previous || `${WORKSPACES.PYMTCNTR}/managePaymentMap`,
      state: {
        messages: [
          {
            id: 2,
            title: modify
              ? locale['wireImports.mapHasBeenModified']
              : locale['wireImports.mapHasBeenCreated'],
            data: { mapName: formState.mapName },
            success
          }
        ]
      }
    });
  };

  const isFormModified = useCallback((formStateData, initialData) => {
    const cleanInitialData = pickBy(castFieldsToNumber(initialData), deleteEmptyField);
    const cleanCurrentData = pickBy(castFieldsToNumber(formStateData), deleteEmptyField);

    return !isEqual(cleanInitialData, cleanCurrentData);
  }, []);

  const onFormSave = () => {
    // Validation for UPDATE_ONLY && ADD_AND_UPDATE
    if (
      processMode === PROCESS_MODES_NAMES.UPDATE_ONLY ||
      processMode === PROCESS_MODES_NAMES.ADD_AND_UPDATE
    ) {
      const atLeastOneValidationPassed = atLeastOneUpdateAndMatchValidation(
        formState,
        setFormErrors,
        locale
      );
      const matchAndUpdateValidationPassed = validateMatchAndUpdateCheckboxes(
        formState,
        setFormErrors,
        locale,
        mapFormat,
        accountTypesData
      );

      if (!atLeastOneValidationPassed || !matchAndUpdateValidationPassed) {
        window.scrollTo(0, 0);
        return null;
      }
    }

    if (transactionFieldVariant === TRANSACTION_FIELD_VARIANT.TRAN_TYPE) {
      const passed = validateTransactionTypeRow({
        accountTypesData,
        formState,
        setFormErrors,
        locale,
        mapFormat,
        processMode
      });

      if (!passed) {
        window.scrollTo(0, 0);
        return null;
      }
    }

    // Validate Form
    return validate().then(({ data, isValid }) => {
      // leave without saving if form is not changed
      if (isValid && !isFormModified(formState, initialData)) {
        setLeavingPage(true);
        return closeModal(true);
      }

      setSending(true);
      if (!isValid) {
        setSending(false);
        return null;
      }

      window.scrollTo(0, 0);
      const dataToSend = formAPIData({ data, processMode, mapFormat, transactionFieldVariant });

      subscriptions.current.saveForm$ = sendACHMap({
        data: dataToSend,
        modify,
        mapCode,
        mapFormat
      }).subscribe((data) => {
        if (!data.success) {
          return handleError(data);
        }
        return handleSuccess(data, modify);
      }, handleError);
      return subscriptions;
    });
  };

  const onClearForm = useCallback(() => {
    setFormErrors([]); // Clear alerts
    storeFormData({ name: mapFormat.toLowerCase() }); // clear this form
    resetErrors(); // clear errors
    resetData(); // update formState
    resetTableComponentKey(Date.now());
    setLeavingPage(true); // allow user to leave
  }, [setLeavingPage, mapFormat, storeFormData, resetErrors, resetData]);

  const onMapFormatChange = useCallback(
    (name, value) => {
      setMapFormat(value);
      setFormErrors([]); // Clear alerts
      resetErrors(); // removes all errors from previously selected form
      resetData(); // resets data option prop on useValidation()

      const clickedType =
        value === MAP_FORMAT.FIXED ? STORE_FORM_ACTIONS.DELIMITED : STORE_FORM_ACTIONS.FIXED;
      const previousType =
        value === MAP_FORMAT.FIXED ? STORE_FORM_ACTIONS.FIXED : STORE_FORM_ACTIONS.DELIMITED;

      const storedHeadersData = {
        [HEADER_FIELDS.MAP_NAME]: formState[HEADER_FIELDS.MAP_NAME],
        [HEADER_FIELDS.ZERO_AMOUNT_FIELDS]: formState[HEADER_FIELDS.ZERO_AMOUNT_FIELDS],
        [HEADER_FIELDS.STRIP_QUOTES]: formState[HEADER_FIELDS.STRIP_QUOTES],
        [HEADER_FIELDS.SKIP_FIRST_RECORD]: formState[HEADER_FIELDS.SKIP_FIRST_RECORD]
      };

      // Store previous Form Data
      storeFormData({
        type: previousType,
        formState: {
          ...storedFormData[previousType],
          ...storedHeadersData
        }
      });

      // Store Current Form
      return storeFormData({
        type: clickedType,
        formState
      });
    },
    [setMapFormat, formState, storeFormData, storedFormData, resetErrors, resetData]
  );

  const onClose = useCallback(
    () => closeModal(!isFormModified(formState, initialData)),
    [closeModal, formState, initialData, isFormModified]
  );

  const changeTransactionVariant = useCallback((...args) => {
    changeTransactionFieldVariant(...args);
    setFormErrors([]);
  }, []);

  useEffect(
    () => () => {
      unsubscribeComponent(subscriptions.current);
    },
    []
  );

  useEffect(() => {
    setLeavingPage(!isFormModified(formState, initialData));
  }, [modify, setLeavingPage, isFormModified, formState, initialData]);

  return (
    <>
      <div className={classes.wrapper}>
        <CheckboxRadioGroup className={classes.checkboxWrapper} type="radio">
          <CheckboxRadio
            checked={mapFormat === MAP_FORMAT.DELIMITED}
            data-qa="wire-import-delimited"
            htmlValue={MAP_FORMAT.DELIMITED}
            inline
            labelText={locale['wireImports.delimited']}
            name="mapFormat"
            onChange={onMapFormatChange}
            type="radio"
          />
          <CheckboxRadio
            checked={mapFormat === MAP_FORMAT.FIXED}
            data-qa="wire-import-fixed"
            htmlValue={MAP_FORMAT.FIXED}
            inline
            labelText={locale['wireImports.fixed']}
            name="mapFormat"
            onChange={onMapFormatChange}
            type="radio"
          />
        </CheckboxRadioGroup>
      </div>

      <FormContextProvider value={formContext}>
        {!!formErrors.length && (
          <div className={classes.alertsWrapper}>
            {formErrors.map(({ code, message }) => (
              <div key={code} className="col-12">
                <Alert
                  dataQa="createACHImport-error"
                  message={message}
                  onHide={() => clearFormError(code, setFormErrors)}
                  variant={ALERTVARIATIONS.ERROR}
                />
              </div>
            ))}
          </div>
        )}
        <Header
          achDelimitersData={achDelimitersData}
          endOfRecordsData={endOfRecordsData}
          formState={formState}
          initialData={initialData}
          mapFormat={mapFormat}
          onChange={onChange}
          sending={sending}
          validate={validate}
        />
        <Table
          key={tableComponentKey}
          accountTypesData={accountTypesData}
          changeTransactionFieldVariant={changeTransactionVariant}
          formState={formState}
          mapFormat={mapFormat}
          modify={modify}
          onChange={onChange}
          processMode={processMode}
          registerValidators={registerValidators}
          sending={sending}
          transactionCodesData={transactionCodesData}
          transactionFieldVariant={transactionFieldVariant}
          validate={validate}
          unregisterValidators={unregisterValidators}
          // for componentDidUpdate
          controlledFormData={controlledFormData}
        />
        <Footer onCancel={onClose} onClear={onClearForm} onSave={onFormSave} sending={sending} />
      </FormContextProvider>
    </>
  );
};

CreateACHImportMapComponent.propTypes = {
  mapCode: PropTypes.string.isRequired,
  setSending: PropTypes.func,
  sending: PropTypes.bool,
  modify: PropTypes.bool,
  setError: PropTypes.func,
  closeModal: PropTypes.func,
  history: PropTypes.shape({}),
  achImportMapData: PropTypes.oneOfType([PropTypes.shape({})]),
  processMode: PropTypes.string,
  achDelimitersData: PropTypes.arrayOf(PropTypes.shape({})),
  endOfRecordsData: PropTypes.arrayOf(PropTypes.shape({})),
  accountTypesData: PropTypes.arrayOf(PropTypes.shape({})),
  transactionCodesData: PropTypes.arrayOf(PropTypes.shape({})),
  setLeavingPage: PropTypes.func
};

export default withRouter(CreateACHImportMapComponent);
